import {
  importProvidersFrom,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { OktaAuthModule } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { AuthService } from './services/auth/auth.service';
import { TokenProviderService } from './services/token-provider/token-provider.service';

export interface OktaAuthEnv {
  issuer: string;
  clientId: string;
  scopes?: string[];
}

// https://developer.okta.com/docs/guides/sign-into-spa-redirect/angular/main/#configure-your-app
const getOktaAuth = (env: OktaAuthEnv) =>
  new OktaAuth({
    ...env,
    redirectUri: window.location.origin + '/login/callback',
    postLogoutRedirectUri: window.location.origin,
    scopes: [
      'openid',
      'profile',
      'offline_access',
      'email',
      ...(env.scopes || []),
    ],
    transformAuthState: async (oktaAuth, authState) => {
      // Adding the much-needed 'initialized' property.
      // This tells our app that it can continue to load whether the user is authenticated or not.
      authState['initialized'] = true;
      return authState;
    },
  });

export const provideOktaAuth = (oktaAuthEnv: OktaAuthEnv) =>
  importProvidersFrom(
    OktaAuthModule.forRoot({ oktaAuth: getOktaAuth(oktaAuthEnv) }),
  );

// Provides token initialization and refresh management.
export const provideTokenManager = () =>
  provideAppInitializer(async () => {
    const tokens = inject(TokenProviderService);
    const authService = inject(AuthService);
    const isAuthenticated = await tokens.isAuthenticated();
    return isAuthenticated ? authService.initialize() : Promise.resolve();
  });
